import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Clips } from '@air/api';
import { ClipType, SortDirection } from '@air/api/types';
import { callAirApi } from '@air/utils-api';

import { toWorkspaceAgentAssetMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentAssetMentionItem';
import {
  type WorkspaceAgentMentionSearchParams,
  type WorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import {
  useWorkspaceAgentMentionSearchQueries,
  useWorkspaceAgentMentionSearchResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionSearchResult';
import { WORKSPACE_AGENT_MENTION_PAGE_SIZE } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';

/** Media + gallery files in one workspace-wide mention stream (backend default omits `nonMedia`). */
export const PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES: ClipType[] = Object.values(ClipType);

export const PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_SEARCH_KEY = 'PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_SEARCH';

export type PrivateWorkspaceAgentAssetMentionSearchParams = {
  workspaceId: string | undefined;
  search: string;
};

export type PrivateWorkspaceAgentAssetMentionSearchKey = [
  typeof PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_SEARCH_KEY,
  PrivateWorkspaceAgentAssetMentionSearchParams,
];

export const getPrivateWorkspaceAgentAssetMentionSearchKey = (
  params: PrivateWorkspaceAgentAssetMentionSearchParams,
): PrivateWorkspaceAgentAssetMentionSearchKey => [PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_SEARCH_KEY, params];

/**
 * Workspace-wide lexical `Clips.search`, cursor paginated. Keyed apart from `useClips`: `getClipsKey`
 * omits `limit`, so the picker's pages would collide with a gallery search.
 */
export const usePrivateWorkspaceAgentAssetMentionSearch = ({
  enabled,
  query,
  workspaceId,
}: WorkspaceAgentMentionSearchParams): WorkspaceAgentMentionSearchResult => {
  const queries = useWorkspaceAgentMentionSearchQueries({ enabled, query });
  const { debouncedQuery } = queries;

  const search = useInfiniteQuery({
    queryKey: getPrivateWorkspaceAgentAssetMentionSearchKey({ search: debouncedQuery, workspaceId }),
    queryFn: !workspaceId
      ? skipToken
      : ({ pageParam }: { pageParam: string }) =>
          callAirApi(() =>
            Clips.search({
              cursor: pageParam || undefined,
              limit: WORKSPACE_AGENT_MENTION_PAGE_SIZE,
              search: debouncedQuery || undefined,
              semantic: false,
              sortField: { direction: SortDirection.desc, name: 'dateModified' },
              types: PRIVATE_WORKSPACE_AGENT_ASSET_MENTION_CLIP_TYPES,
              workspaceId,
            }),
          ),
    enabled: !!workspaceId && enabled,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor ?? undefined,
    initialPageParam: '',
  });

  const { data } = search;

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data.clips.map(toWorkspaceAgentAssetMentionItem)),
    [data],
  );

  return useWorkspaceAgentMentionSearchResult({ items, queries, search });
};
